// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { X, Search, Camera, TrendingDown, ArrowRight, Sparkles } from 'lucide-react';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * Free User Welcome Modal
 * Shown to newly registered free users on first login per session.
 * Provides a quick overview and gently prompts upgrade to Pro.
 */
const FreeWelcomeModal = ({ onClose, userName, onUpgrade }) => {
  const trapRef = useFocusTrap({ active: true, onEscape: onClose });

  const handleUpgrade = () => {
    onClose();
    if (onUpgrade) onUpgrade();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="free-modal-title"
    >
      <div
        ref={trapRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
            <TrendingDown className="w-8 h-8 text-white" />
          </div>
          <h2 id="free-modal-title" className="text-3xl font-bold mb-1">
            Welcome to PricePAL Compare! 🎉
          </h2>
          <p className="text-green-100 font-medium">Start comparing prices and saving money today</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* How It Works */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              How PricePAL Compare Works
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <div className="text-3xl mb-2">📷</div>
                <div className="font-bold text-gray-800">Scan or Search</div>
                <div className="text-gray-500 text-xs mt-1">Scan a barcode or search by product name</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <div className="text-3xl mb-2">🏪</div>
                <div className="font-bold text-gray-800">Compare Stores</div>
                <div className="text-gray-500 text-xs mt-1">See prices across Tesco, Asda, Aldi & more</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <div className="text-3xl mb-2">💰</div>
                <div className="font-bold text-gray-800">Save Money</div>
                <div className="text-gray-500 text-xs mt-1">Shop at the cheapest store every time</div>
              </div>
            </div>
          </section>

          {/* What You Get on Standard Free */}
          <section className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h3 className="text-md font-bold text-gray-900 mb-3">Your Standard Free plan includes:</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-green-500">✓</span>
                <span className="text-gray-700">5 barcode/product scans per day</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-green-500">✓</span>
                <span className="text-gray-700">3 active price alerts</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-green-500">✓</span>
                <span className="text-gray-700">2 receipt submissions per month</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-green-500">✓</span>
                <span className="text-gray-700">Basic price comparison across UK supermarkets</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-green-500">✓</span>
                <span className="text-gray-700">Basic Fuel Price Finder</span>
              </div>
            </div>
          </section>

          {/* Gentle Upgrade Prompt */}
          <section className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚡</span>
              </div>
              <div className="flex-1">
                <h3 className="text-md font-bold text-blue-900 mb-1">Want unlimited access?</h3>
                <p className="text-blue-700 text-sm mb-3">
                  Upgrade to <strong>Premium from just £1.99/mo</strong> for unlimited scans, price alerts, 
                  smart shopping lists, and more.
                </p>
                <button
                  onClick={handleUpgrade}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition"
                >
                  View Pro Plans
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>

          {/* Help */}
          <section className="text-center">
            <p className="text-gray-500 text-sm">
              Questions? Go to <strong>More {'>'} Support</strong> or email{' '}
              <strong>support@pricepalcompare.com</strong>
            </p>
          </section>
        </div>

        {/* Footer Action */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-lg font-bold py-4 rounded-xl shadow-lg hover:from-green-600 hover:to-emerald-700 hover:scale-[1.02] transition-all transform flex items-center justify-center gap-2"
          >
            <span>🎉</span>
            Open PricePAL Compare
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeWelcomeModal;
